import clsx from 'clsx';
import { Link } from '@/lib/i18n/navigation';

import styles from './HistoryNavigations.module.scss';

export interface HistoryNavigationLink {
  sys: { id: string };
  /** Top label text */
  topLabelText?: string | null;
  /** Link text */
  urlText?: string | null;
  /** Link URL */
  url?: string | null;
  /** Link target (_self, _blank) */
  urlTarget?: string | null;
}

export interface HistoryNavigationsProps {
  /** Collection of navigation links */
  linksCollection?: {
    items: Array<HistoryNavigationLink | null>;
  } | null;
  /** Optional className */
  className?: string;
}

/**
 * Chevron icon for navigation links
 */
function ChevronIcon({ className }: { className?: string }) {
  return (
    <svg className={className} width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
      <path d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6-1.41-1.41z" />
    </svg>
  );
}

/**
 * History navigation links component
 *
 * Displays navigation links with labels, typically used for
 * previous/next navigation in history or timeline contexts.
 *
 * @example
 * <HistoryNavigations
 *   linksCollection={{
 *     items: [
 *       { sys: { id: '1' }, topLabelText: 'Previous', urlText: '1920s', url: '/history/1920s' },
 *       { sys: { id: '2' }, topLabelText: 'Next', urlText: '1940s', url: '/history/1940s' },
 *     ]
 *   }}
 * />
 */
export function HistoryNavigations({ linksCollection, className }: HistoryNavigationsProps) {
  const links =
    linksCollection?.items.filter((item): item is HistoryNavigationLink => item !== null) ?? [];

  if (links.length === 0) {
    return null;
  }

  const isSpaceBetween = links.length > 1;

  return (
    <div
      className={clsx(
        styles.wrapper,
        isSpaceBetween ? styles.spaceBetween : styles.centered,
        className
      )}
      data-testid="HistoryNavigationWrapper"
    >
      {links.map((item) => (
        <div key={item.sys.id} className={styles.navigations}>
          <div className={styles.navigationsTitle}>{item.topLabelText}</div>
          <div className={styles.linksBox}>
            <Link href={item.url ?? '#'} target={item.urlTarget ?? '_self'} className={styles.link}>
              {item.urlText}
            </Link>
            <ChevronIcon className={styles.chevron} />
          </div>
        </div>
      ))}
    </div>
  );
}

HistoryNavigations.displayName = 'HistoryNavigations';
